import React, { useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import ConfigFieldPanel from 'components/admin/shared/ConfigFieldPanel';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import FormHeader from 'components/shared/form/FormHeader';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import Launcher from 'components/shared/Launcher';
import { AI_PROMPT_TARGET_OBJECTS, AI_PROMPT_TYPES } from 'constants/aiPrompts';
import { EPISODE, PLAN_TYPE } from 'constants/classifications';
import { PROVIDER } from 'constants/groupTypes';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { dateAtTime, simpleDateAtTime } from 'lib/date';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { PERMISSIONS } from 'models/flexible/Profile';
import {
  adminAIPromptsQueryKeys,
  adminShowAIPrompt,
  createAIPrompt,
  updateAIPrompt,
} from 'services/api/admin/aiPrompts';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import type { AdminAIPromptShowFields } from 'services/api/fields';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

import AIPromptPublishModal from './AIPromptPublishModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

type ConfigFieldType = 'explainer' | 'instructions' | 'outputDefinition';

const CONFIG_FIELD_LABELS: Record<ConfigFieldType, { title: string; placeholder: string }> = {
  explainer: { title: 'Explainer', placeholder: 'Enter explainer text...' },
  instructions: { title: 'Instructions', placeholder: 'Enter instructions...' },
  outputDefinition: { title: 'Output Structure', placeholder: 'Enter output structure...' },
};

const SHOW_INCLUDES = ['client', 'groupTypes', 'planTypeClassifications', 'episodeClassifications'] as const;

const schema = yup.object({
  name: yup.string().required('Name is required'),
  type: yup.mixed().test('type', 'Type is required', (v: any) => !!v?.id),
  targetClassname: yup.mixed().test('targetClassname', 'Target Object is required', (v: any) => !!v?.id),
  client: yup.mixed().test('client', 'Client is required', (v: any) => !!v?.id),
  config: yup.object({
    explainer: yup.string().required('Explainer is required'),
    instructions: yup.string().required('Instructions are required'),
    outputDefinition: yup.string().required('Output Structure is required'),
  }),
});

const schemaPublished = yup.object({
  name: yup.string().required('Name is required'),
  config: yup.object({
    explainer: yup.string().required('Explainer is required'),
  }),
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditAIPrompt() {
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const isCopyMode = searchParams.get('copy') === 'true';
  const isEditing = !!id && !isCopyMode;
  const { addToast } = useToastActions();
  const { profile } = useProfile();

  const canEdit = profile.has(PERMISSIONS.adminAIPromptEdit);
  const canPublish = profile.has(PERMISSIONS.adminAIPromptPublish);
  const readOnly = !!id && !isCopyMode && !canEdit;

  // --- Data + form ---

  // Track published/locked state via ref so it's available to useAdminForm config
  // without circular references. The ref is updated after each render and the hook
  // reads config from a ref internally, so schema/toPayload always see the latest value.
  const isPublishedRef = useRef(false);
  const lockedRef = useRef(readOnly);

  const form = useAdminForm({
    id,
    show: adminShowAIPrompt,
    create: createAIPrompt,
    update: isCopyMode ? (_id: string, payload: any) => createAIPrompt(payload) : updateAIPrompt,
    include: SHOW_INCLUDES,
    queryKey: adminAIPromptsQueryKeys,
    schema: isPublishedRef.current ? schemaPublished : schema,
    toFormValues: (data) => ({
      name: data?.name ?? '',
      description: data?.description ?? '',
      type: data?.type ? (AI_PROMPT_TYPES.find((opt) => opt.id === data.type) ?? null) : null,
      targetClassname: data?.targetClassname
        ? (AI_PROMPT_TARGET_OBJECTS.find((opt) => opt.id === data.targetClassname) ?? null)
        : null,
      client: isCopyMode ? null : (data?.client ?? null),
      groupIds: data?.groupIds ?? [],
      groupTypes: data?.groupTypes ?? [],
      planTypeClassifications: data?.planTypeClassifications ?? [],
      episodeClassifications: data?.episodeClassifications ?? [],
      config: {
        explainer: data?.config?.explainer ?? '',
        instructions: data?.config?.instructions ?? '',
        outputDefinition: data?.config?.outputDefinition ?? '',
        explainerLastUpdated: data?.config?.explainerLastUpdated,
        instructionsLastUpdated: data?.config?.instructionsLastUpdated,
        outputDefinitionLastUpdated: data?.config?.outputDefinitionLastUpdated,
      },
      isPublished: (data?.isPublished && !isCopyMode) || false,
    }),
    toPayload: (v) => {
      if (lockedRef.current) {
        return {
          description: v.description,
          explainer: v.config?.explainer,
        };
      }
      return {
        name: v.name ?? '',
        description: v.description ?? '',
        type: v.type?.id ?? '',
        targetClassname: v.targetClassname?.id ?? '',
        clientId: v.client?.id ?? '',
        groupIds: v.groupIds ?? [],
        groupTypeIds: v.groupTypes.map((gt) => gt.id),
        planTypeClassificationIds: v.planTypeClassifications.map((c) => c.id),
        episodeClassificationIds: v.episodeClassifications.map((c) => c.id),
        explainer: v.config?.explainer ?? '',
        instructions: v.config?.instructions ?? '',
        outputDefinition: v.config?.outputDefinition ?? '',
        include: SHOW_INCLUDES.join(','),
      };
    },
    initiallyDirty: isCopyMode,
    onCreateSuccess: { toast: 'AI prompt successfully added!', navigate: '/admin/ai-prompts' },
    onUpdateSuccess: isCopyMode
      ? { toast: 'AI prompt successfully added!', navigate: '/admin/ai-prompts' }
      : { toast: 'AI prompt successfully updated!' },
    onError: { toast: 'There was an error saving the AI prompt. Please try again.' },
  });

  const { values, errors, valid, dirty, setValue, submit, loading } = form;
  const isPublished = !!form.data?.isPublished && !isCopyMode;
  const isProcessPromptComplete =
    !form.data?.latestProcessPromptStatus || form.data.latestProcessPromptStatus === 'completed';
  const locked = readOnly || (isPublished && !isCopyMode);

  // Keep refs in sync for the next render's config
  isPublishedRef.current = isPublished;
  lockedRef.current = locked;

  // --- Dropdown options ---

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'aiPrompts', { clientType: !isPublished ? 'Admin' : undefined }],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc', ...(!isPublished ? { clientType: 'Admin' } : {}) },
  });

  const groupTypeOpts = useInfiniteOptions({
    queryKey: ['adminGroupTypes', 'provider'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc', type: PROVIDER },
  });

  const planTypeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'planType'],
    fetchFn: adminIndexClassifications,
    params: { type: PLAN_TYPE, sortBy: 'name asc' },
  });

  const episodeTypeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'episode'],
    fetchFn: adminIndexClassifications,
    params: { type: EPISODE, sortBy: 'name asc' },
  });

  // --- Local state ---

  const [submitting, setSubmitting] = useState(false);
  const [publishingPrompt, setPublishingPrompt] = useState<AIPrompt<AdminAIPromptShowFields> | null>(null);
  const [activeSlideout, setActiveSlideout] = useState<ConfigFieldType | null>(null);

  const getFieldSubLabel = (fieldType: ConfigFieldType) => {
    const lastUpdated = values.config?.[`${fieldType}LastUpdated`];
    return lastUpdated ? `Last updated: ${simpleDateAtTime(lastUpdated)}` : 'Last updated: —';
  };

  const handleSubmit = async (publishAfter = false) => {
    setSubmitting(true);
    try {
      await submit();
      if (publishAfter) {
        // Re-fetch to get the returned prompt for publish modal
        const returned = await adminShowAIPrompt(id!, undefined, SHOW_INCLUDES);
        if (returned.isProcessPromptComplete) {
          setPublishingPrompt(returned);
        } else {
          addToast({
            text: 'The prompt is still being processed. Please wait for processing to complete before publishing.',
          });
          form.goBack();
        }
      } else {
        form.goBack();
      }
    } catch {
      // Error toast is handled by useAdminForm's onError lifecycle
    } finally {
      setSubmitting(false);
    }
  };

  const slideoutReadOnly = activeSlideout === 'explainer' ? !isCopyMode && !canEdit : locked;

  // --- Render ---

  if (!!id && loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader
        title={readOnly ? 'View AI Prompt' : isEditing ? 'Edit AI Prompt' : 'Add AI Prompt'}
        subtitle={
          (isEditing || readOnly) && (
            <PublishedSubtitle>
              Published: <StatusDot $on={isPublished} />
              {isPublished ? 'Yes' : 'No'}
              {isPublished
                ? form.data?.publishedAt && (
                    <>
                      <UnboldedSpan>|</UnboldedSpan>
                      Published At:<UnboldedSpan>{dateAtTime(form.data.publishedAt)}</UnboldedSpan>
                    </>
                  )
                : form.data?.updatedAt && (
                    <>
                      <UnboldedSpan>|</UnboldedSpan>
                      Last Updated:<UnboldedSpan>{dateAtTime(form.data.updatedAt)}</UnboldedSpan>
                    </>
                  )}
            </PublishedSubtitle>
          )
        }
      />

      <InputGroup title='Prompt Type' error={getFieldError(errors, 'type')}>
        <Select
          options={AI_PROMPT_TYPES}
          value={values.type ?? null}
          onChange={(v) => setValue('type', v)}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Select Prompt Type'
          disabled={locked}
        />
      </InputGroup>

      <InputGroup title='Name' error={errors.name}>
        <Input
          value={values.name ?? ''}
          onChange={(e) => setValue('name', e.target.value)}
          placeholder='Enter Name'
          $hasError={!!errors.name}
          disabled={locked}
        />
      </InputGroup>

      <InputGroup title='Description (optional)'>
        <StyledTextarea
          value={values.description ?? ''}
          onChange={(e) => setValue('description', e.target.value)}
          placeholder='Enter description'
          disabled={!isCopyMode && !canEdit}
        />
      </InputGroup>

      <InputGroup title='Target Object' error={getFieldError(errors, 'targetClassname')}>
        <Select
          options={AI_PROMPT_TARGET_OBJECTS}
          value={values.targetClassname ?? null}
          onChange={(v) => setValue('targetClassname', v)}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Select Target Object'
          disabled={locked}
        />
      </InputGroup>

      <InputGroup title='Client'>
        <Select
          {...clientOpts}
          value={values.client ?? null}
          onChange={(v) => setValue('client', v)}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Select Client'
          isClearable
          disabled={locked}
        />
      </InputGroup>

      <InputGroup>
        <MultiSelectChips
          title='Levels of Care'
          {...groupTypeOpts}
          values={values.groupTypes ?? []}
          onChange={(v) => setValue('groupTypes', v)}
          getOptionValue={getId}
          getOptionLabel={getDisplayName}
          placeholder='Levels of Care'
          disabled={locked}
        />
      </InputGroup>

      <InputGroup>
        <MultiSelectChips
          title='Plan Types'
          {...planTypeOpts}
          values={values.planTypeClassifications ?? []}
          onChange={(v) => setValue('planTypeClassifications', v)}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Plan Types'
          disabled={locked}
        />
      </InputGroup>

      <InputGroup>
        <MultiSelectChips
          title='Episode Types'
          {...episodeTypeOpts}
          values={values.episodeClassifications ?? []}
          onChange={(v) => setValue('episodeClassifications', v)}
          getOptionValue={getId}
          getOptionLabel={getName}
          placeholder='Episode Types'
          disabled={locked}
        />
      </InputGroup>

      <SectionHeader>{values.type?.name ? `${values.type.name} Prompt` : 'Configuration'}</SectionHeader>

      {(['instructions', 'outputDefinition', 'explainer'] as const).map((fieldType) => (
        <Launcher
          key={fieldType}
          label={CONFIG_FIELD_LABELS[fieldType].title}
          subLabel={getFieldSubLabel(fieldType)}
          handleLaunch={() => setActiveSlideout(fieldType)}
        />
      ))}

      {errors['config.explainer'] && <FieldError>{errors['config.explainer']}</FieldError>}
      {errors['config.instructions'] && <FieldError>{errors['config.instructions']}</FieldError>}
      {errors['config.outputDefinition'] && <FieldError>{errors['config.outputDefinition']}</FieldError>}

      <ButtonGroup style={{ marginTop: 24 }}>
        {readOnly ? (
          <Button onClick={form.goBack}>Go Back</Button>
        ) : (
          <>
            {isEditing && canPublish && (
              <Button
                variant='outline-primary'
                onClick={() => handleSubmit(true)}
                disabled={!dirty || (!valid && dirty) || submitting || !isProcessPromptComplete}
                loading={submitting}>
                Update and Publish
              </Button>
            )}
            <Spacer />
            <Button variant='ghost' onClick={form.goBack}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={isEditing ? !dirty || (!valid && dirty) || submitting : !valid || submitting}
              loading={submitting}>
              {isEditing ? 'Update AI Prompt' : 'Create AI Prompt'}
            </Button>
          </>
        )}
      </ButtonGroup>

      <Foldout open={!!activeSlideout} width='min(85%, 917px)'>
        {activeSlideout && (
          <ConfigFieldPanel
            title={CONFIG_FIELD_LABELS[activeSlideout].title}
            placeholder={CONFIG_FIELD_LABELS[activeSlideout].placeholder}
            value={values.config?.[activeSlideout] ?? ''}
            readOnly={slideoutReadOnly}
            onApply={(val) => setValue('config', { ...values.config, [activeSlideout]: val })}
            onClose={() => setActiveSlideout(null)}
          />
        )}
      </Foldout>

      {publishingPrompt && (
        <AIPromptPublishModal
          isOpen={!!publishingPrompt}
          aiPrompt={publishingPrompt}
          onCancel={() => {
            setPublishingPrompt(null);
            form.goBack();
          }}
          onComplete={() => {
            setPublishingPrompt(null);
            form.goBack();
          }}
        />
      )}
    </AdminFormPage>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const StyledTextarea = styled.textarea`
  width: 100%;
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  padding: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: none;
  min-height: 80px;

  &::placeholder {
    color: ${colors.black25};
  }

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const FieldError = styled.div`
  font-size: 12px;
  color: ${colors.red100};
  margin-top: -8px;
  margin-bottom: 12px;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;

const PublishedSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.div<{ $on: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 8px;
  background-color: ${({ $on }) => ($on ? colors.green100 : colors.black25)};
  margin-right: 4px;
`;

const UnboldedSpan = styled.span`
  font-weight: 400;
`;

const Spacer = styled.div`
  flex: 1;
`;
